import React, { useEffect, useState } from "react";
import { Stage, Layer, Text, Image } from "react-konva";
import { db } from "./firebase";
import { ref, onValue } from "firebase/database";
import useImage from "./useImage";
import VideoOverlay from "./components/VideoOverlay";

// مكون منفصل للصور
function ImageLayer({ layer }) {
  const [image] = useImage(layer.content);
  return (
    <Image
      x={layer.x}
      y={layer.y}
      image={image}
      width={layer.width}
      height={layer.height}
      opacity={layer.opacity}
      rotation={layer.rotation}
      draggable
    />
  );
}

// مكون منفصل للـ GIF
function GifLayer({ layer }) {
  const [image] = useImage(layer.content);
  return (
    <Image
      x={layer.x}
      y={layer.y}
      image={image}
      width={layer.width}
      height={layer.height}
      opacity={layer.opacity}
      rotation={layer.rotation}
      draggable
    />
  );
}

// مكون منفصل للفيديو
function VideoLayer({ layer }) {
  return (
    <rect
      x={layer.x}
      y={layer.y}
      width={layer.width}
      height={layer.height}
      opacity={layer.opacity}
      rotation={layer.rotation}
      draggable
      fill="#202024"
      stroke="#ac76f4"
      strokeWidth={2}
    />
  );
}

export default function StreamView() {
  // دالة للتحقق من رؤية الطبقة بناءً على المجموعات
  const isLayerVisible = (layer, canvas) => {
    // إذا كانت الطبقة مخفية مباشرة، ارجع false
    if (layer.visible === false) return false;
    
    // تحقق من المجموعات
    const layerGroups = canvas.groups?.filter(group => group.layers && group.layers.includes(layer.id)) || [];
    
    // إذا كانت الطبقة في مجموعة مخفية، ارجع false
    for (const group of layerGroups) {
      if (group.visible === false) return false;
    }
    
    return true;
  };
  const [canvas, setCanvas] = useState({
    width: 1280,
    height: 720,
    backgroundType: "color",
    backgroundValue: "#0b1437",
    opacity: 1,
    gradient: { from: "#e91e63", to: "#009688", angle: 90 },
    layers: [],
    visible: true,
    transparentBackground: true
  });

  useEffect(() => {
    return onValue(ref(db, "canvas"), (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        setCanvas({
          ...val,
          gradient: val.gradient || { from: "#e91e63", to: "#009688", angle: 90 },
          opacity: val.opacity === undefined ? 1 : val.opacity,
          layers: Array.isArray(val.layers) ? val.layers : [],
          groups: Array.isArray(val.groups) ? val.groups : [],
          visible: val.visible !== false
        });
      }
    });
  }, []);

  const getBackground = () => {
    // إذا كان النوع "none" أو الشفافية مفعلة، ارجع شفاف
    if (canvas.backgroundType === "none" || canvas.transparentBackground !== false) {
      return "transparent";
    }
    
    if (canvas.backgroundType === "gradient") {
      return `linear-gradient(${canvas.gradient.angle}deg, ${canvas.gradient.from}, ${canvas.gradient.to})`;
    }
    
    return canvas.backgroundValue || "transparent";
  };

  if (!canvas.visible) return null;

  return (
    <div style={{ 
      width: "100vw", 
      height: "100vh", 
      background: getBackground(),
      position: "relative",
      overflow: "hidden"
    }}>
      <Stage
        width={canvas.width}
        height={canvas.height}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: canvas.opacity
        }}
      >
        <Layer>
          {canvas.layers
            .filter((layer) => isLayerVisible(layer, canvas))
            .map((layer) => {
              if (layer.type === "text") {
                const padding = layer.backgroundPadding || 10;
                const radius = layer.backgroundRadius || 0;
                const bgOpacity = layer.backgroundOpacity || 1;
                
                return (
                  <React.Fragment key={layer.id}>
                    {/* خلفية النص */}
                    {layer.backgroundType === "color" && (
                      <rect
                        x={layer.x - padding}
                        y={layer.y - padding}
                        width={layer.width + (padding * 2)}
                        height={layer.height + (padding * 2)}
                        fill={layer.backgroundColor || "#000000"}
                        opacity={bgOpacity * layer.opacity}
                        rotation={layer.rotation}
                        cornerRadius={radius}
                        draggable
                      />
                    )}
                    
                    {/* النص */}
                    <Text
                      x={layer.x}
                      y={layer.y}
                      text={layer.content}
                      fontSize={layer.fontSize}
                      fontFamily={layer.font}
                      fontWeight={layer.fontWeight}
                      fill={layer.color}
                      opacity={layer.opacity}
                      rotation={layer.rotation}
                      draggable
                    />
                  </React.Fragment>
                );
              }
              if (layer.type === "image") {
                return <ImageLayer key={layer.id} layer={layer} />;
              }
              if (layer.type === "gif") {
                return <GifLayer key={layer.id} layer={layer} />;
              }
              if (layer.type === "video") {
                return <VideoLayer key={layer.id} layer={layer} />;
              }
              return null;
            })}
        </Layer>
      </Stage>

      {/* معالجة الفيديو والصوت */}
      <VideoOverlay layers={canvas.layers} selectedId={null} showAudioControls={false} isStreamView={true} canvas={canvas} />
      
      {/* فيديو خلفية النص */}
      {canvas.layers
        .filter(l => l.type === "text" && l.backgroundType === "video" && l.backgroundVideo && isLayerVisible(l, canvas))
        .map(layer => {
          const padding = layer.backgroundPadding || 10;
          const bgOpacity = layer.backgroundOpacity || 1;
          
          return (
            <video
              key={`text-bg-video-${layer.id}`}
              src={layer.backgroundVideo}
              autoPlay loop muted playsInline
              style={{
                position: "absolute",
                left: layer.x - padding,
                top: layer.y - padding,
                width: layer.width + (padding * 2),
                height: layer.height + (padding * 2),
                opacity: bgOpacity * layer.opacity,
                zIndex: 1,
                borderRadius: layer.backgroundRadius || 0,
                transform: `rotate(${layer.rotation || 0}deg)`,
                transformOrigin: "center center",
                pointerEvents: "none"
              }}
            />
          );
        })}
    </div>
  );
}
